"use client";
import React, { useEffect, useState } from "react";

import { useRouter } from "next/navigation";

import { Button } from "@heroui/button";
import { Checkbox } from "@heroui/checkbox";
import { Divider } from "@heroui/divider";
import { MapPin } from "lucide-react";

import { PageFormTemplate } from "../components/PageFormTemplate";

import {
  FormInput,
  FormSelect,
  FormTextarea,
} from "@/components/forms/components/FormInputs";
import { registerCaseWorkerAction } from "@/lib/actions/case-worker-registration";
import { EventKeys } from "@/lib/observability/catalog";
import { routes } from "@/lib/routes";
import { OrganizationTerminology } from "@/lib/services/terminologyService";
import type { ActionResponse } from "@/lib/utils/errors";
import { buildUrl } from "@/lib/utils/url";
import {
  CaseWorkerRegistrationData,
  caseWorkerRegistrationSchema,
} from "@/lib/validations/case-worker-registration";

interface SimpleArea {
  id: string;
  name: string;
  state: string;
}

interface Organization {
  id: string;
  name: string;
}

interface CaseWorkerRegistrationFormProps {
  areas: SimpleArea[];
  organization: Organization;
  registerCaseWorker?: typeof registerCaseWorkerAction;
  terminology: OrganizationTerminology;
}

export function CaseWorkerRegistrationForm({
  areas,
  organization,
  registerCaseWorker = registerCaseWorkerAction,
  terminology,
}: CaseWorkerRegistrationFormProps) {
  // Serialize props to ensure they are client-safe and stable
  const simpleOrganization = { id: organization.id, name: organization.name };
  const simpleAreas = areas.map((area) => ({
    id: area.id,
    name: area.name,
    state: area.state,
  }));

  const router = useRouter();
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);

  // Group areas by state for better organization
  const areasByState = simpleAreas.reduce(
    (acc, area) => {
      if (!acc[area.state]) {
        acc[area.state] = [];
      }
      acc[area.state].push(area);

      return acc;
    },
    {} as Record<string, SimpleArea[]>,
  );

  const handleAreaChange = (areaId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedAreas((prev) => [...prev, areaId]);
    } else {
      setSelectedAreas((prev) => prev.filter((id) => id !== areaId));
    }
  };

  // Form submission handler
  const handleFormSubmit = async (data: CaseWorkerRegistrationData) => {
    if (selectedAreas.length === 0) {
      // Better error handling for missing area selection
      if (typeof window !== "undefined") {
        // Scroll to area selection section
        const areaSection = document.querySelector(
          '[data-testid="area-selection-section"]',
        );

        if (areaSection) {
          areaSection.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }

      return {
        error: "Please select at least one area to serve as a case worker",
        success: false,
      };
    }

    const result = await registerCaseWorker({
      ...data,
      areaIds: selectedAreas,
      organizationId: simpleOrganization.id,
    });

    // The server action already returns a standardized ActionResponse
    return result;
  };

  const defaultValues: CaseWorkerRegistrationData = {
    agency: "",
    areaIds: [],
    church: "",
    city: "",
    confirmPassword: "",
    country: "US",
    email: "",
    experience: "",
    first_name: "",
    last_name: "",
    password: "",
    phone: "",
    primaryAreaId: "",
    state: "",
    street_address: "",
    zip_code: "",
  };

  return (
    <PageFormTemplate
      defaultValues={defaultValues}
      description="Join as a case worker to help connect families with volunteer support in your community"
      eventKeys={{
        attempt: EventKeys.ORG_CASE_WORKER_REGISTRATION_FORM_SUBMIT_ATTEMPT,
        failure: EventKeys.ORG_CASE_WORKER_REGISTRATION_FORM_SUBMIT_FAILURE,
        success: EventKeys.ORG_CASE_WORKER_REGISTRATION_FORM_SUBMIT_SUCCESS,
      }}
      formSchema={caseWorkerRegistrationSchema}
      submitLabel="Submit Application"
      title={`Register as Case Worker - ${simpleOrganization.name}`}
      onSubmit={handleFormSubmit}
      onSuccess={(result: ActionResponse<{ email: string }>) => {
        // Redirect with the actual email from the result
        if (result?.success && result.data?.email) {
          const redirectUrl = buildUrl(
            `/registration-submitted?email=${encodeURIComponent(
              result.data.email,
            )}&organization=${encodeURIComponent(simpleOrganization.name)}`,
          );

          setTimeout(() => {
            router.push(redirectUrl);
          }, 1000);
        }
      }}
    >
      {({ control, setValue }) => {
        useEffect(() => {
          if (setValue) {
            setValue("areaIds", selectedAreas, { shouldValidate: true });
          }
        }, [selectedAreas, setValue]);

        return (
          <>
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">
                What Case Workers Do:
              </h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Post needs for families requiring assistance</li>
                <li>• Coordinate with volunteers who offer help</li>
                <li>• Track progress and mark needs as completed</li>
                <li>
                  • Serve as a bridge between families and community support
                </li>
              </ul>
            </div>

            <div className="mb-4 text-center">
              <Button
                className="text-sm"
                color="default"
                variant="light"
                onPress={() => router.push(routes.auth.login())}
              >
                Already have an account? Sign In
              </Button>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-default-900">
                Personal Information
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormInput
                  required
                  control={control}
                  label="First Name"
                  name="first_name"
                  placeholder="Enter your first name"
                />

                <FormInput
                  required
                  control={control}
                  label="Last Name"
                  name="last_name"
                  placeholder="Enter your last name"
                />
              </div>

              <FormInput
                required
                control={control}
                label="Email Address"
                name="email"
                placeholder="Enter your email address"
                type="email"
              />

              <FormInput
                required
                control={control}
                label="Phone Number"
                name="phone"
                placeholder="Enter your phone number"
                type="tel"
              />

              <FormInput
                required
                control={control}
                label="Agency"
                name="agency"
                placeholder="Enter your agency name"
              />

              <FormInput
                control={control}
                label="Church You Attend"
                name="church"
                placeholder="Enter church name (optional)"
              />
            </div>

            <Divider className="my-6" />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-default-900">
                Address Information
              </h3>
              <p className="text-sm text-default-600">
                We collect address information to better coordinate case worker
                services in your area.
              </p>

              <FormInput
                required
                control={control}
                label="Street Address"
                name="street_address"
                placeholder="Enter street address"
                startContent={
                  <MapPin
                    aria-hidden="true"
                    className="text-default-400 h-4 w-4"
                  />
                }
              />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FormInput
                  required
                  control={control}
                  label="City"
                  name="city"
                  placeholder="Enter city"
                  startContent={
                    <MapPin
                      aria-hidden="true"
                      className="text-default-400 h-4 w-4"
                    />
                  }
                />

                <FormInput
                  required
                  control={control}
                  label="State"
                  name="state"
                  placeholder="CA"
                  startContent={
                    <MapPin
                      aria-hidden="true"
                      className="text-default-400 h-4 w-4"
                    />
                  }
                />

                <FormInput
                  required
                  control={control}
                  label="ZIP Code"
                  name="zip_code"
                  placeholder="12345"
                  startContent={
                    <MapPin
                      aria-hidden="true"
                      className="text-default-400 h-4 w-4"
                    />
                  }
                />
              </div>

              <FormSelect
                required
                control={control}
                data-testid="primary-area-select"
                description={`This should be the ${terminology.area.singular.toLowerCase()} where your physical address is located or where you typical work.`}
                label={`Primary ${terminology.area.singular}`}
                name="primaryAreaId"
                options={(selectedAreas.length > 0
                  ? simpleAreas.filter((area) =>
                      selectedAreas.includes(area.id),
                    )
                  : simpleAreas
                ).map((area) => ({
                  label: `${area.name} (${area.state})`,
                  value: area.id,
                }))}
                placeholder={`Select your primary ${terminology.area.singular.toLowerCase()}`}
              />
            </div>

            <Divider className="my-6" />

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-default-900">
                Account Security
              </h3>

              <FormInput
                required
                control={control}
                label="Password"
                name="password"
                placeholder="Create a secure password"
                type="password"
              />

              <FormInput
                required
                control={control}
                label="Confirm Password"
                name="confirmPassword"
                placeholder="Confirm your password"
                type="password"
              />

              <FormTextarea
                control={control}
                label="Relevant Experience (Optional)"
                minRows={3}
                name="experience"
                placeholder="Briefly describe any relevant experience with social services, community work, etc."
              />
            </div>

            <Divider className="my-6" />

            {/* Areas Selection */}
            <div className="space-y-4" data-testid="area-selection-section">
              <div>
                <h3 className="text-lg font-medium text-default-900 mb-2">
                  Select Areas to Serve{" "}
                  <span className="text-danger-500">*</span>
                </h3>
                <p className="text-sm text-default-600 mb-4">
                  Choose the areas where you'd like to serve as a case worker
                  for {simpleOrganization.name}. You can select multiple areas
                  within this organization.{" "}
                  <strong>At least one area is required.</strong>
                </p>
              </div>

              {Object.entries(areasByState).map(([state, stateAreas]) => (
                <div
                  key={state}
                  className="border border-default-200 rounded-lg p-4"
                >
                  <h4 className="font-medium text-default-800 mb-3">{state}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {stateAreas.map((area) => (
                      <Checkbox
                        key={area.id}
                        data-testid="area-checkbox"
                        isSelected={selectedAreas.includes(area.id)}
                        onValueChange={(isSelected) =>
                          handleAreaChange(area.id, isSelected)
                        }
                      >
                        <span className="text-sm">{area.name}</span>
                      </Checkbox>
                    ))}
                  </div>
                </div>
              ))}

              {selectedAreas.length === 0 && (
                <div className="bg-danger-50 border border-danger-200 rounded-lg p-3 text-sm text-danger-700">
                  <strong>⚠️ Required:</strong> Please select at least one area
                  to serve
                </div>
              )}

              {selectedAreas.length > 0 && (
                <div className="bg-success-50 border border-success-200 rounded-lg p-3 text-sm text-success-700">
                  <strong>✓ Selected:</strong> {selectedAreas.length} area
                  {selectedAreas.length !== 1 ? "s" : ""}
                </div>
              )}
            </div>

            <Divider className="my-6" />

            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800">
              <p className="font-medium mb-1">Application Process:</p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Submit your application using this form</li>
                <li>
                  Local coordinators and administrators will review your
                  application
                </li>
                <li>You'll receive an email notification with the decision</li>
                <li>
                  If approved, you'll gain access to the case worker dashboard
                </li>
              </ol>
            </div>
          </>
        );
      }}
    </PageFormTemplate>
  );
}
